/**
 * MobileSearchResults Component
 * 
 * This component renders a simplified grid of product cards for mobile devices.
 * It handles loading states and empty results cases.
 * 
 * Unlike desktop view which uses ProductCard, this component has its own 
 * inline rendering of products with a different layout optimized for mobile.
 */

import { Link } from "react-router-dom";
import { Loader2, Store as StoreIcon, MapPin } from "lucide-react";

interface MobileSearchResultsProps {
  products: any[];
  isLoadingProducts: boolean;
}

export const MobileSearchResults = ({ 
  products,
  isLoadingProducts
}: MobileSearchResultsProps) => {
  if (isLoadingProducts) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-brand-primary" />
      </div>
    );
  }

  if (!products || products.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-brand-primary/70">No products found</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-2 px-2">
      {products.map((product) => (
        <Link
          key={product?.uniqueId || product?.id || Math.random().toString()}
          to={`/product/${product?.id}${product?.selectedVariant ? `/${product.selectedVariant.id}` : ''}`}
          className="block bg-white border border-brand-primary rounded-lg overflow-hidden"
        >
          <div className="aspect-square w-full relative">
            <img 
              src={product?.images?.[0] || '/images/placeholder-product.png'} 
              alt={product?.title || 'Product'}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="p-2">
            <h3 className="text-[13px] text-brand-secondary truncate">
              {product?.title || 'Product'}
            </h3>
            <p className="text-[13px] text-brand-secondary">
              {product?.selectedVariant?.size_name || 'Standard'}
            </p>
            <p className="text-[13px] text-brand-secondary">
              ${product?.price || 0}
            </p>
            <div className="flex flex-col gap-1 mt-2">
              <div className="inline-flex items-center bg-white border border-[#967F81]/20 rounded-full px-2 py-1">
                <StoreIcon className="h-3 w-3 mr-1 text-[#2C1810]" />
                <span className="text-[11px] text-[#2C1810]">
                  {product?.store?.store_name || 'Arthurs Florist'}
                </span>
              </div>
              <div className="inline-flex items-center bg-[#967F81]/30 border border-[#967F81] rounded-full px-2 py-1">
                <MapPin className="h-3 w-3 mr-1 text-[#2C1810] flex-shrink-0" />
                <span className="text-[11px] text-[#2C1810] whitespace-nowrap">
                  {product?.store?.suburb ? `${product.store.suburb}, ${product.store.state}` : 'Surry Hills, NSW'}
                </span>
              </div>
            </div>
          </div>
        </Link>
      ))}
    </div>
  );
};